import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { CustomAttributeFilterRequest } from '@/modules/common/types/dto/request/custom-attribute-request';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import {
  GetGeneratedBillsRequest,
  GetGeneratedBillsResponse,
} from '@/modules/finance/bills/types/dto';
import { axiosInstance } from '@/utils/axios';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { mapGeneratedBillsFromResponse } from '../mappers';
import { GenerateBillListItem } from '../types';

interface GetGenerateBillsDataParams {
  cursor?: number;
  filters?: {
    searchString?: string;
    billingPeriod?: string;
    memberIds?: number[];
    projectIds?: number[];
    customAttributeFilters?: CustomAttributeFilterRequest[];
  };
}

/**
 * Retrieves generated bill data for members within a date range.
 * Wraps GET /api/bills/generated with server-side filtering.
 *
 * @param params - Cursor and filters (billingPeriod, searchString, memberIds, projectIds)
 * @returns Paginated list of generated member bills
 */
export const getGenerateBillsData = async ({
  filters = {},
}: GetGenerateBillsDataParams): Promise<PaginatedResponse<GenerateBillListItem>> => {
  const { searchString, billingPeriod, memberIds, projectIds, customAttributeFilters } = filters;

  if (!billingPeriod || typeof billingPeriod !== 'string') {
    return {
      items: [],
      pagination: {
        total: 0,
        cursor: null,
      },
    };
  }

  const dateRange = parseDateRange(billingPeriod);
  if (!dateRange?.startDate || !dateRange?.endDate) {
    return {
      items: [],
      pagination: {
        total: 0,
        cursor: null,
      },
    };
  }

  const request: GetGeneratedBillsRequest = {
    startDate: normalizeToStartOfDayUTC(dateRange.startDate).toISOString(),
    endDate: normalizeToStartOfDayUTC(dateRange.endDate).toISOString(),
    ...(searchString ? { search: searchString } : {}),
    ...(memberIds?.length ? { memberIds: memberIds.join(',') } : {}),
    ...(projectIds?.length ? { projectIds: projectIds.join(',') } : {}),
    ...(customAttributeFilters?.length
      ? { customAttributeFilters: JSON.stringify(customAttributeFilters) }
      : {}),
  };

  const endpoint = '/api/bills/generated';
  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.BILLS_GENERATED_LOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: GetGeneratedBillsResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: request,
      })
  );

  const items = mapGeneratedBillsFromResponse(response);

  return {
    items,
    pagination: {
      total: items.length,
      cursor: null,
    },
  };
};
